import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";

function MedicalForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const userIdFromUrl = searchParams.get("userId");
  const userId = userIdFromUrl || localStorage.getItem("userId");

  // Add validation for userId
  useEffect(() => {
    if (!userId) {
      // toast.error("User ID is missing. Please log in again.");
      console.log("error");

      // navigate("/login"); // Redirect to login if userId is missing
    }
  }, [userId, navigate]);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      id: 0,
      firstRegistrationId: 0,
      createdBy: userId || "",
      updatedBy: userId || "",
      medicalConditions: {},
      medicalConditionsPart2: {},
      medicalConditionsPart3: {},
      medicalOtherInformations: "",
      medicalAuthorizations1: {},
      medicalAuthorizations2: {},
      studentMedicalDocuments: [],
    },
  });

  const [pupils, setPupils] = useState([]);
  const [selectedPupilId, setSelectedPupilId] = useState(null);
  const [pupilError, setPupilError] = useState(false);
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [medicalConditionsPart2, setMedicalConditionsPart2] = useState([]);
  const [medicalConditionsPart3, setMedicalConditionsPart3] = useState([]);
  const [medicalAuthorizations1, setMedicalAuthorizations1] = useState([]);
  const [medicalAuthorizations2, setMedicalAuthorizations2] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const getContactsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }
    try {
      const response = await get(
        `Student/GetStudentFamilyInformationByUserId?userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response && response.length > 0) {
        setContactsData(response);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchPupils = async () => {
    try {
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPupils(response);
      // if (response.length > 0) {
      //   const firstPupilId = response[0].id;
      //   setSelectedPupilId(firstPupilId);
      //   handlePupilSelection({ target: { value: firstPupilId } });
      // }
      if (response && response.length > 0) {
        const firstPupilId = response[response.length - 1].id;
        setSelectedPupilId(firstPupilId);
        handlePupilSelection({ target: { value: firstPupilId } });
      } else {
        setSelectedPupilId(null);
      }
    } catch (error) {
      console.error("Error fetching pupils:", error);
    }
  };

  const fetchMedicalInfoCheckboxes = async () => {
    try {
      const response = await get(
        "StudentMedicalInformation/GetMedicalCondition"
      );
      if (response) {
        const medicalPart1 = response.medicalConditions.filter(
          (condition) => condition.id <= 13
        );
        const medicalPart2 = response.medicalConditions.filter(
          (condition) => condition.id >= 14 && condition.id < 23
        );
        const medicalPart3 = response.medicalConditions.filter(
          (condition) => condition.id >= 23
        );
        const authPart1 = response.medicalAuthorizations.filter(
          (condition) => condition.id <= 5
        );
        const authPart2 = response.medicalAuthorizations.filter(
          (condition) => condition.id >= 6
        );

        setMedicalConditions(medicalPart1);
        setMedicalConditionsPart2(medicalPart2);
        setMedicalConditionsPart3(medicalPart3);
        setMedicalAuthorizations1(authPart1);
        setMedicalAuthorizations2(authPart2);

        // Initialize form state for all checkboxes
        const formData = {
          medicalConditions: Object.fromEntries(
            medicalPart1.map((c) => [c.id, false])
          ),
          medicalConditionsPart2: Object.fromEntries(
            medicalPart2.map((c) => [c.id, false])
          ),
          medicalConditionsPart3: Object.fromEntries(
            medicalPart3.map((c) => [c.id, false])
          ),
          medicalAuthorizations1: Object.fromEntries(
            authPart1.map((a) => [a.id, false])
          ),
          medicalAuthorizations2: Object.fromEntries(
            authPart2.map((a) => [a.id, false])
          ),
        };
        reset(formData, { keepDefaultValues: true });
      }
    } catch (error) {
      console.error("Error fetching medical information:", error);
    }
  };

  useEffect(() => {
    getContactsList();
    fetchPupils();
    fetchMedicalInfoCheckboxes();
  }, []);

  const handlePupilSelection = async (event) => {
    const pupilId = event.target.value;
    setSelectedPupilId(pupilId);
    setPupilError(false);
    if (!pupilId) {
      toast.error("Please select a pupil.");
      setPupilError(true);
      return;
    }

    try {
      const response = await get(
        `StudentMedicalInformation/GetStudentMedicalInformationsByFirstRegistrationId?studentFirstRegistrationId=${pupilId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Initialize all checkboxes to false
      const formData = {
        id: response.id || 0,
        firstRegistrationId: response.firstRegistrationId || parseInt(pupilId),
        createdBy: response.createdBy || userId || "",
        updatedBy: userId || "",
        medicalConditions: Object.fromEntries(
          medicalConditions.map((c) => [c.id, false])
        ),
        medicalConditionsPart2: Object.fromEntries(
          medicalConditionsPart2.map((c) => [c.id, false])
        ),
        medicalConditionsPart3: Object.fromEntries(
          medicalConditionsPart3.map((c) => [c.id, false])
        ),
        medicalOtherInformations: response.medicalOtherInformations || "",
        medicalAuthorizations1: Object.fromEntries(
          medicalAuthorizations1.map((a) => [a.id, false])
        ),
        medicalAuthorizations2: Object.fromEntries(
          medicalAuthorizations2.map((a) => [a.id, false])
        ),
        studentMedicalDocuments: [],
      };

      // Set true for conditions/authorizations from API response
      (response.medicalConditions || []).forEach((condition) => {
        if (condition.id > 0 && !condition.isDeleted) {
          if (condition.id <= 13) {
            formData.medicalConditions[condition.id] = true;
          } else if (condition.id >= 14 && condition.id < 23) {
            formData.medicalConditionsPart2[condition.id] = true;
          } else if (condition.id >= 23) {
            formData.medicalConditionsPart3[condition.id] = true;
          }
        }
      });

      (response.medicalAuthorizations || []).forEach((auth) => {
        if (auth.id > 0 && !auth.isDeleted) {
          if (auth.id <= 5) {
            formData.medicalAuthorizations1[auth.id] = true;
          } else {
            formData.medicalAuthorizations2[auth.id] = true;
          }
        }
      });

      // Load documents
      const documentsFromAPI = response.studentMedicalDocuments || [];
      const formattedDocuments = documentsFromAPI.map((doc) => ({
        fileName: doc.fileName,
        filePath: doc.filePath,
        documentType: doc.documentType,
        base64String: doc.base64String || "",
      }));
      setUploadedFiles(formattedDocuments);

      // Reset form
      reset(formData);

      console.log("Form state after reset:", getValues()); // Debug
    } catch (error) {
      console.error("Error fetching medical information:", error);
    }
  };

  const validateAtLeastOne = () => {
    const values = getValues("medicalAuthorizations2") || {};
    return (
      Object.values(values).some((checked) => checked) ||
      "This field is required."
    );
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }
    if (!selectedPupilId) {
      toast.error("Please select a pupil.");
      setPupilError(true);
      return;
    }
    setPupilError(false);

    const medicalConditionsPayload = [
      ...medicalConditions
        .filter((condition) => data.medicalConditions?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
      ...medicalConditionsPart2
        .filter((condition) => data.medicalConditionsPart2?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
      ...medicalConditionsPart3
        .filter((condition) => data.medicalConditionsPart3?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
    ];

    const medicalAuthorizationsPayload = [
      ...medicalAuthorizations1
        .filter((condition) => data.medicalAuthorizations1?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
      ...medicalAuthorizations2
        .filter((condition) => data.medicalAuthorizations2?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
    ];

    const formData = {
      ...data,
      id: data.id,
      firstRegistrationId: parseInt(selectedPupilId),
      createdBy: data.createdBy || userId,
      updatedBy: userId,
      medicalConditions: medicalConditionsPayload,
      medicalOtherInformations: data.medicalOtherInformations || "",
      medicalAuthorizations: medicalAuthorizationsPayload,
      studentMedicalDocuments: uploadedFiles,
    };

    console.log("Saving data:", formData); // Debug
    // return;

    try {
      const response = await post(
        "StudentMedicalInformation/UpdateStudentMedicalInformation",
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.isSuccess) {
        toast.success("Success!");
        if (roleId === "3") {
          navigate("/all-registrations");
        } else {
          navigate("/");
        }
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error saving medical information:", error);
      toast.error("Failed to save medical information.");
    }
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const filePromises = files.map((file, index) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            fileName: `Medical Form-${
              uploadedFiles.length + index + 1
            }.${file.name.split(".").pop()}`,
            filePath: reader.result,
            documentType: file.type || "application/octet-stream",
            base64String: reader.result.split(",")[1],
          });
        };
        reader.readAsDataURL(file);
      });
    });

    const fileDataArray = await Promise.all(filePromises);
    setUploadedFiles((prev) => [...prev, ...fileDataArray]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePreviewFile = (file) => {
    const isPDF = file.fileName.toLowerCase().endsWith(".pdf");
    if (file.base64String) {
      const byteCharacters = atob(file.base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: isPDF ? "application/pdf" : "image/jpeg",
      });
      const fileUrl = URL.createObjectURL(blob);
      window.open(fileUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
    } else if (file.filePath) {
      const attachmentUrl =
        process.env.REACT_APP_LIVE_DOMAIN_URL +
        "/ExternalImages/" +
        file.filePath;
      window.open(attachmentUrl, "_blank");
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Medical Information</h2>
      </div>

      <div className="card">
        <div className="card-body new-form-style">
          <form className="row g-3 " onSubmit={handleSubmit(saveData)}>
            <div className="col">
              <div style={{ width: "300px" }}>
                <label className="form-label form-label-new">
                  Select Pupil <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select ${pupilError ? "is-invalid" : ""}`}
                  onChange={handlePupilSelection}
                  value={selectedPupilId || ""}
                >
                  <option value="">Select</option>
                  {pupils.map((pupil) => (
                    <option key={pupil.id} value={pupil.id}>
                      {pupil.firstName}
                    </option>
                  ))}
                </select>
                {pupilError && (
                  <small className="text-danger">This field is required.</small>
                )}
              </div>
            </div>

            <h5 className="mb-0 p-2 px-3 mt-4 text-white bg-brand rounded-pill">
              Parent's Details
            </h5>

            <div className="row gx-3 gy-2">
              {contactsData.map((contact, index) => (
                <div className="col-md-3" key={index}>
                  <div className="card h-100">
                    <div className="card-header fs-8 px-3 py-2 bg-light fw-bold">
                      <strong>{contact.relationShip}</strong>
                      {contact.isPrimary && (
                        <span className="badge bg-primary fs-9 ms-2">
                          Primary Contact
                        </span>
                      )}
                    </div>
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item fs-9">
                        <strong>Name:</strong> {contact.firstName}{" "}
                        {contact.lastName}
                      </li>
                      <li className="list-group-item fs-9">
                        <strong>Email:</strong> {contact.email}
                      </li>
                      <li className="list-group-item fs-9">
                        <strong>Phone:</strong> {contact.phone}
                      </li>
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <h5 className="mb-0 p-2 px-3 mt-4 text-white bg-brand rounded-pill">
              Medical Conditions
            </h5>

            <div className="col-md-4">
              <h5 className="fs-9 mb-2">Does your child suffer from</h5>
              {medicalConditions.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalConditions.${condition.id}`)}
                      type="checkbox"
                      id={`condition-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="col-md-4">
              <h5 className="fs-9 mb-2">
                Has your child ever had any of the following diseases
              </h5>
              {medicalConditionsPart2.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalConditionsPart2.${condition.id}`)}
                      type="checkbox"
                      id={`condition-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="col-md-4">
              <h5 className="fs-9 mb-2">
                Has your child been inoculated against
              </h5>
              {medicalConditionsPart3.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalConditionsPart3.${condition.id}`)}
                      type="checkbox"
                      id={`condition-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="col-md-12">
              <h5 className="fs-9 mb-2">
                If you answered "Yes" to any of the above medical conditions,
                please provide further details.
              </h5>
              <textarea
                {...register("medicalOtherInformations")}
                className="form-control"
                placeholder=""
              ></textarea>
              {errors.medicalOtherInformations && (
                <small className="text-danger">
                  {errors.medicalOtherInformations.message}
                </small>
              )}
            </div>

            <h5 className="mb-0 p-2 px-3 mt-4 text-white bg-brand rounded-pill">
              Authorisation
            </h5>

            <div className="row gy-2">
              <div className="col-md-6">
                <h5 className="fs-9 mb-2">
                  I hereby authorise the school to administer the below chosen
                  listed medications at the discretion of the Medical staff.
                </h5>
                {medicalAuthorizations1.map((condition) => (
                  <div key={condition.id}>
                    <label className="form-check fs-8">
                      <input
                        {...register(`medicalAuthorizations1.${condition.id}`)}
                        type="checkbox"
                        id={`auth-${condition.id}`}
                        className="form-check-input"
                      />
                      <span className="d-inline-block fs-9">
                        {condition.name}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
              <div className="col-md-6">
                <h5 className="fs-9 mb-2">
                  In case of an emergency, we kindly ask your approval on any of
                  the following: <span className="text-danger fs-8">*</span>
                </h5>
                {medicalAuthorizations2.map((condition) => (
                  <div key={condition.id}>
                    <label className="form-check fs-8">
                      <input
                        {...register(`medicalAuthorizations2.${condition.id}`, {
                          validate: validateAtLeastOne,
                        })}
                        type="checkbox"
                        id={`auth-${condition.id}`}
                        className="form-check-input"
                      />
                      <span className="d-inline-block fs-9">
                        {condition.name}
                      </span>
                    </label>
                  </div>
                ))}
                {errors.medicalAuthorizations2 && (
                  <small className="text-danger">
                    {Object.values(errors.medicalAuthorizations2)[0]?.message}
                  </small>
                )}
              </div>
            </div>

            <h5 className="mb-0 p-2 px-3 mt-4 text-white bg-brand rounded-pill">
              Medical Documents
            </h5>

            <div className="row gy-2">
              <div className="col-md-4">
                <label className="form-label">Upload Documents</label>
                <div className="attachment-wrapper">
                  <div className="attachment-placeholder">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      className="form-control mt-2"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      style={{ width: "100px" }}
                    />
                  </div>
                  {uploadedFiles.length > 0 && (
                    <div className="attachment-preview mt-2">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="file-item mb-1 d-flex justify-content-between align-items-center"
                        >
                          <div className="att-file-name">{file.fileName}</div>
                          <div className="d-flex gap-1">
                            <span
                              className="preview-btn"
                              onClick={() => handlePreviewFile(file)}
                            >
                              <i className="fa fa-eye fs-10"></i>
                            </span>
                            <span
                              className="remove-btn"
                              onClick={() => handleRemoveFile(index)}
                            >
                              <i className="fa fa-remove fs-10"></i>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 mt-5">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span
                    className="spinner-border spinner-custom-size me-1"
                    aria-hidden="true"
                  ></span>
                ) : null}
                <span role="status">Save</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default MedicalForm;
